<script setup>
/**
 * Deals — the pipeline board salespeople work the day out of.
 *
 * One pipeline at a time, because the columns are that pipeline's stages: the
 * picker only appears once the account has more than one board to choose
 * between. Switching pipelines refetches; nothing filters on the client.
 *
 * A drop is the only mutation here, and it goes through `change_stage` — the
 * single server-side seam that owns the won/lost timestamps and the one-way
 * customer promotion.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import { useAlert } from 'dashboard/composables';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import OpportunityBoard from 'dashboard/components-next/Opportunities/OpportunityBoard.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { useOpportunitiesStore } from 'dashboard/stores/opportunities';

const { t } = useI18n();

const store = useOpportunitiesStore();
const { pipelines, uiFlags } = storeToRefs(store);

const hasError = ref(false);

const boardColumns = computed(() => store.boardColumns);

const pipelineOptions = computed(() =>
  pipelines.value.map(pipeline => ({
    value: pipeline.id,
    label: pipeline.name,
  }))
);

const isEmpty = computed(
  () => !uiFlags.value.isFetchingPipelines && !pipelines.value.length
);

const load = async () => {
  hasError.value = false;
  try {
    await store.fetchOpportunities();
  } catch (error) {
    hasError.value = true;
  }
};

const selectedPipelineId = computed({
  get: () => store.activePipelineId ?? '',
  set: value => {
    store.setActivePipeline(value);
    load();
  },
});

// The deals are fetched for whichever pipeline the store settled on, so the two
// requests are sequential rather than parallel.
const loadBoard = async () => {
  hasError.value = false;
  try {
    await store.fetchPipelines();
  } catch (error) {
    hasError.value = true;
    return;
  }
  await load();
};

const onMove = async ({ opportunity, stage }) => {
  try {
    await store.moveToStage(opportunity.id, stage);
  } catch (error) {
    useAlert(t('OPPORTUNITIES.BOARD.MOVE_ERROR'));
  }
};

onMounted(loadBoard);
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-h-0 gap-4 px-6 py-5 overflow-hidden bg-n-background"
    data-testid="opportunities-view"
  >
    <div class="flex items-center justify-between gap-4">
      <h1 class="mb-0 text-lg font-medium text-n-slate-12">
        {{ t('OPPORTUNITIES.HEADER.TITLE') }}
      </h1>

      <label
        v-if="pipelineOptions.length > 1"
        class="flex items-center gap-2 mb-0 text-sm text-n-slate-11"
      >
        {{ t('OPPORTUNITIES.HEADER.PIPELINE') }}
        <Select
          v-model="selectedPipelineId"
          :options="pipelineOptions"
          data-testid="opportunity-pipeline-select"
        />
      </label>
    </div>

    <EmptyStateLayout
      v-if="isEmpty && !hasError"
      compact
      icon="i-lucide-kanban"
      :title="t('OPPORTUNITIES.BOARD.NO_PIPELINES.TITLE')"
      :subtitle="t('OPPORTUNITIES.BOARD.NO_PIPELINES.SUBTITLE')"
      data-testid="opportunity-board-no-pipelines"
    />

    <div v-else class="flex flex-col min-h-0 grow">
      <OpportunityBoard
        :columns="boardColumns"
        :is-loading="uiFlags.isFetchingList"
        :has-error="hasError"
        @move="onMove"
        @retry="loadBoard"
      />
    </div>
  </section>
</template>
